import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ContentCard from "@/components/feature/ContentCard";
import { useSEO } from "@/hooks/useSEO";
import { getHotContents, searchContents } from "@/lib/content";
import type { ContentType } from "@/types/content";

const FILTERS: { value: ContentType | "all"; label: string }[] = [
  { value: "all", label: "全部" },
  { value: "video", label: "趣味视频" },
  { value: "joke", label: "搞笑段子" },
  { value: "interview", label: "轻松访谈" },
];

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const query = params.get("q") ?? "";
  const [input, setInput] = useState(query);
  const [type, setType] = useState<ContentType | "all">("all");

  useSEO({
    title: query ? `“${query}” 的搜索结果 - 羞羞网站` : "搜索内容 - 羞羞网站",
    description: query
      ? `在羞羞网站搜索“${query}”的相关娱乐内容，包含趣味视频、搞笑段子与轻松访谈。`
      : "在羞羞网站搜索你感兴趣的趣味视频、搞笑段子和轻松访谈内容。",
    keywords: "羞羞网站,搜索,娱乐内容,搞笑视频,段子",
  });

  useEffect(() => {
    setInput(query);
    setType("all");
  }, [query]);

  const results = useMemo(() => searchContents(query, type), [query, type]);
  const hot = getHotContents(3);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const kw = input.trim();
    setParams(kw ? { q: kw } : {});
  };

  return (
    <div className="w-full bg-background-50">
      <Navbar />
      <main className="pt-16">
        <section className="w-full px-4 md:px-8 pt-10 pb-8 bg-background-100 border-b border-background-200">
          <h1 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950">
            搜索内容
          </h1>
          <p className="text-sm text-foreground-600 mt-2">
            输入关键词，快速找到你想要的趣味视频、搞笑段子或轻松访谈。
          </p>

          <form onSubmit={submit} className="mt-6 flex flex-col sm:flex-row gap-3 max-w-2xl">
            <div className="flex items-center gap-2 flex-1 rounded-md bg-background-50 border border-background-300 px-4 py-3 focus-within:border-primary-400">
              <i className="ri-search-line text-foreground-500 text-lg" />
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="例如：打工人、搞笑、解压"
                className="bg-transparent outline-none text-sm w-full text-foreground-900 placeholder:text-foreground-400"
              />
            </div>
            <button
              type="submit"
              className="px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              搜索
            </button>
          </form>
        </section>

        <section className="w-full px-4 md:px-8 py-8">
          <div className="flex flex-wrap items-center gap-2 mb-6">
            {FILTERS.map((f) => (
              <button
                key={f.value}
                type="button"
                onClick={() => setType(f.value)}
                className={`px-4 py-2 rounded-full text-xs font-medium transition-colors whitespace-nowrap ${
                  type === f.value
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-100 text-foreground-700 hover:bg-background-200"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>

          {query ? (
            <p className="text-sm text-foreground-600 mb-6">
              关键词 <strong className="text-primary-600">“{query}”</strong> 共找到{" "}
              <strong className="text-foreground-950">{results.length}</strong> 条结果
            </p>
          ) : (
            <p className="text-sm text-foreground-600 mb-6">还没输入关键词，先看看全部内容吧。</p>
          )}

          {results.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
              {results.map((item) => (
                <ContentCard key={item.id} item={item} />
              ))}
            </div>
          ) : (
            <div className="rounded-lg border border-dashed border-background-300 bg-background-50 py-16 px-6">
              <div className="flex flex-col items-center text-center">
                <span className="w-16 h-16 rounded-full bg-secondary-100 text-secondary-900 flex items-center justify-center">
                  <i className="ri-search-eye-line text-3xl" />
                </span>
                <h3 className="font-heading font-semibold text-lg text-foreground-950 mt-5">
                  没有找到“{query}”相关内容
                </h3>
                <p className="text-sm text-foreground-600 mt-2 max-w-md">
                  换个关键词试试，或者看看下面这些大家都喜欢的内容。
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mt-10 max-w-4xl mx-auto">
                {hot.map((item) => (
                  <ContentCard key={item.id} item={item} />
                ))}
              </div>

              <div className="flex justify-center mt-8">
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md border border-background-300 text-foreground-800 text-sm font-medium hover:border-primary-400 hover:text-primary-600 transition-colors whitespace-nowrap"
                >
                  <i className="ri-home-5-line" />
                  返回首页
                </Link>
              </div>
            </div>
          )}
        </section>
      </main>
      <Footer />
    </div>
  );
}